/*
  FIND LOCAL iframe modal controller.

  Listens for delegated clicks on `[data-find-local-trigger]` buttons (rendered
  by common-find-local-button.liquid + injected by plp-searchanise-controller.js
  for /search cards). On click, reads the URL template + modal title from the
  global mount element (rendered once from theme.liquid via common-find-local-
  dialog.liquid), replaces {product_group} and {variants} with the clicked button's
  data-product-group and data-variants values, builds an overlay + modal + iframe in vanilla DOM
  and appends to <body>.

  Close handlers: X button, backdrop click, Escape key. Body scroll is locked
  while the modal is open. The overlay element is destroyed on close so the
  iframe loads fresh on each open.
*/
(function () {
  'use strict';

  let settings = null;

  function readSettings() {
    if (settings) return settings;
    const mount = document.querySelector('[data-find-local-mount]');
    if (!mount) return null;
    settings = {
      urlTemplate: mount.dataset.findLocalUrlTemplate || '',
      modalTitle: mount.dataset.findLocalModalTitle || '',
      closeLabel: mount.dataset.findLocalCloseLabel || 'Close',
    };
    return settings;
  }

  function buildIframeSrc(template, productGroup, variants) {
    return template
      .replace('{product_group}', encodeURIComponent(productGroup || ''))
      .replace('{variants}', encodeURIComponent(variants || ''));
  }

  function openModal(productGroup, variants) {
    const cfg = readSettings();
    if (!cfg || !cfg.urlTemplate) return;

    /* Remember what had focus so we can restore it when the modal closes
       (WCAG 2.4.3 Focus Order). */
    const opener = document.activeElement;

    const iframeSrc = buildIframeSrc(cfg.urlTemplate, productGroup, variants);

    const overlay = document.createElement('div');
    overlay.className = 'c-find-local-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', cfg.modalTitle);

    const modal = document.createElement('div');
    modal.className = 'c-find-local-modal';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'c-find-local-modal__close';
    closeBtn.setAttribute('aria-label', cfg.closeLabel);
    closeBtn.textContent = '×';

    const title = document.createElement('h2');
    title.className = 'c-find-local-modal__title';
    title.textContent = cfg.modalTitle;

    const iframeWrap = document.createElement('div');
    iframeWrap.className = 'c-find-local-modal__iframe-wrap';

    const iframe = document.createElement('iframe');
    iframe.className = 'c-find-local-modal__iframe';
    iframe.src = iframeSrc;
    iframe.setAttribute('title', cfg.modalTitle);
    iframe.setAttribute('allow', 'geolocation');
    iframe.setAttribute('loading', 'lazy');

    iframeWrap.appendChild(iframe);
    modal.appendChild(closeBtn);
    modal.appendChild(title);
    modal.appendChild(iframeWrap);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    /* Lock body scroll. Scrollbar-gutter: stable on html (critical.css) keeps
       the gutter reserved so the layout below doesn't shift. */
    document.body.style.overflow = 'hidden';

    /* Two rAF ticks: first paints the closed-state element, second flips the
       data-open attribute so the browser sees a transition between states.
       Without the double-rAF the element is appended + the attribute is set
       in the same frame and no transition fires. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.dataset.open = 'true';
      });
    });

    let closing = false;

    function close() {
      if (closing) return;
      closing = true;
      overlay.dataset.open = 'false';

      function onTransitionEnd(event) {
        if (event.target !== overlay || event.propertyName !== 'opacity') return;
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('focusin', onFocusIn);
        overlay.removeEventListener('transitionend', onTransitionEnd);
        overlay.remove();
        /* Return focus to the element that opened the modal. */
        if (opener && typeof opener.focus === 'function') {
          opener.focus();
        }
      }
      overlay.addEventListener('transitionend', onTransitionEnd);
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        close();
      }
    }

    /* Focus trap: the modal is aria-modal, but Tab can still physically leave it
       (especially past the cross-origin iframe). If focus lands outside the
       overlay while it is open, pull it back to the close button. */
    function onFocusIn(event) {
      if (!closing && !overlay.contains(event.target)) {
        closeBtn.focus();
      }
    }

    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);

    closeBtn.focus();
  }

  /* Searchanise cards carry no product group; fetch the PDP's own button
     markup (templates/product.find-local.liquid) and read its data. */
  function fetchProductData(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('view', 'find-local');
    return fetch(url.href, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('find-local view ' + response.status);
        return response.text();
      })
      .then(function (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const button = doc.querySelector('[data-find-local-trigger]');
        return {
          productGroup: (button && button.dataset.productGroup) || '',
          variants: (button && button.dataset.variants) || '',
        };
      });
  }

  function onDocumentClick(event) {
    const trigger = event.target.closest('[data-find-local-trigger]');
    if (!trigger) return;
    event.preventDefault();
    const productGroup = trigger.dataset.productGroup || '';
    const productUrl = trigger.dataset.productUrl || '';
    if (productGroup || !productUrl) {
      openModal(productGroup, trigger.dataset.variants || '');
      return;
    }
    if (trigger.getAttribute('aria-busy') === 'true') return;
    trigger.setAttribute('aria-busy', 'true');
    fetchProductData(productUrl)
      .then(function (data) {
        trigger.dataset.productGroup = data.productGroup;
        trigger.dataset.variants = data.variants;
        openModal(data.productGroup, data.variants);
      })
      .catch(function () {
        openModal('', '');
      })
      .finally(function () {
        trigger.removeAttribute('aria-busy');
      });
  }

  function init() {
    if (!document.querySelector('[data-find-local-mount]')) return;
    document.addEventListener('click', onDocumentClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
